(function () {
  'use strict';

  const OVERLAY_ID = 'nav-search-overlay';
  const OPEN_ATTR = 'data-open';
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'textarea:not([disabled])',
    'select:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(', ');
  let lastFocused = null;

  function getOverlay() {
    return document.getElementById(OVERLAY_ID);
  }

  function getSearchInput(overlay) {
    return overlay.querySelector('input[type="search"]');
  }

  function getFocusables(overlay) {
    return Array.from(overlay.querySelectorAll(FOCUSABLE_SELECTOR)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function openOverlay() {
    const overlay = getOverlay();
    if (!overlay) return;
    lastFocused = document.activeElement;
    overlay.setAttribute(OPEN_ATTR, 'true');
    overlay.removeAttribute('aria-hidden');
    overlay.removeAttribute('inert');
    document.documentElement.classList.add('nav-search-open');
    const input = getSearchInput(overlay);
    if (input) {
      setTimeout(() => input.focus(), 40);
    }
  }

  function closeOverlay() {
    const overlay = getOverlay();
    if (!overlay) return;
    // Clean Canvas's predictive-search adds body.overlay-predictive-search
    // (overflow: hidden) when its dropdown opens, and wires its own close
    // handler to a .js-search-overlay backdrop we hide. Call its close()
    // directly so that body class — and the predictive-search open state —
    // both clear. Defensive body-class removal after for browsers where the
    // custom element isn't ready yet.
    const predictive = overlay.querySelector('predictive-search[open]');
    if (predictive && typeof predictive.close === 'function') {
      try { predictive.close(); } catch (e) { /* ignore */ }
    }
    document.body.classList.remove('overlay-predictive-search');
    overlay.removeAttribute(OPEN_ATTR);
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('inert', '');
    document.documentElement.classList.remove('nav-search-open');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
  }

  function handleGlobalKey(e) {
    const overlay = getOverlay();
    if (!overlay) return;
    const isOpen = overlay.getAttribute(OPEN_ATTR) === 'true';

    if (!isOpen && e.key === '/' && !isTypingInField(e.target)) {
      e.preventDefault();
      openOverlay();
      return;
    }
    if (isOpen && e.key === 'Escape') {
      e.preventDefault();
      closeOverlay();
      return;
    }
    if (isOpen && e.key === 'Tab') {
      trapTab(e, overlay);
    }
  }

  function trapTab(e, overlay) {
    const focusables = getFocusables(overlay);
    if (focusables.length === 0) {
      e.preventDefault();
      return;
    }
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;
    if (e.shiftKey) {
      if (active === first || !overlay.contains(active)) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (active === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  function isTypingInField(el) {
    if (!el) return false;
    const tag = el.tagName;
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
  }

  function onChipClick(e) {
    const chip = e.target.closest('.nav-search-overlay__chip');
    if (!chip) return;
    const q = chip.getAttribute('data-query');
    const href = chip.getAttribute('data-href');
    if (href) {
      // Close before navigating so scroll-lock + inert state are cleaned up.
      // Without this, the page is navigated away with .nav-search-open still on
      // <html>; back-forward cache (bfcache) restores that exact state on Back,
      // leaving the restored page unable to scroll.
      closeOverlay();
      window.location.href = href;
      return;
    }
    if (q) {
      const overlay = getOverlay();
      const input = overlay && getSearchInput(overlay);
      if (input) {
        input.value = q;
        input.dispatchEvent(new Event('input', { bubbles: true }));
        input.focus();
      }
    }
  }

  function onBackdropClick(e) {
    const overlay = getOverlay();
    if (!overlay) return;
    if (e.target === overlay) closeOverlay();
  }

  function bindTriggers() {
    document.addEventListener('click', function (e) {
      const trigger = e.target.closest('[data-nav-search-trigger]');
      if (trigger) {
        e.preventDefault();
        openOverlay();
        return;
      }
      const close = e.target.closest('[data-nav-search-close]');
      if (close) {
        e.preventDefault();
        closeOverlay();
      }
    });
  }

  /**
   * Lightweight query preprocessing for predictive-search.
   *
   * Native Shopify search is keyword/prefix matching — prepositional queries
   * like "fishing rod for mahi" fail because "for" tokenizes as its own word
   * and dilutes the score. This strips a small set of stopwords *only* in the
   * URL sent to /search/suggest.html — the visible input stays as the user
   * typed it. Synonyms/typo correction live in the Shopify Search & Discovery
   * app (admin), not here.
   */
  const QUERY_STOPWORDS = new Set([
    'for', 'with', 'to', 'a', 'an', 'the', 'and', 'or', 'of', 'in', 'on', 'my', 'me'
  ]);
  function preprocessQuery(raw) {
    if (!raw || typeof raw !== 'string') return raw;
    const tokens = raw.toLowerCase().trim().split(/\s+/);
    const cleaned = tokens.filter(function (t) { return !QUERY_STOPWORDS.has(t); });
    return cleaned.length > 0 ? cleaned.join(' ') : raw;
  }

  function patchFetchForSearch() {
    if (!window.fetch || window.__chaosSearchFetchPatched) return;
    window.__chaosSearchFetchPatched = true;
    const orig = window.fetch;
    window.fetch = function (input, init) {
      try {
        const urlStr = typeof input === 'string' ? input : (input && input.url) || '';
        if (/\/search\/suggest(\.json|\.html)?/.test(urlStr)) {
          const u = new URL(urlStr, window.location.origin);
          const q = u.searchParams.get('q');
          if (q) {
            // Preserve any "product_type:X AND <term>" prefix the theme injects
            const split = q.split(' AND ');
            const last = split[split.length - 1];
            const cleaned = preprocessQuery(last);
            if (cleaned !== last) {
              split[split.length - 1] = cleaned;
              u.searchParams.set('q', split.join(' AND '));
              if (typeof input === 'string') {
                input = u.toString();
              } else if (input instanceof Request) {
                input = new Request(u.toString(), input);
              }
            }
          }
        }
      } catch (e) { /* fall through to original fetch */ }
      return orig.apply(this, arguments);
    };
  }

  function resetOverlayState() {
    document.documentElement.classList.remove('nav-search-open');
    document.body.classList.remove('overlay-predictive-search');
    const overlay = getOverlay();
    if (!overlay) return;
    overlay.removeAttribute(OPEN_ATTR);
    overlay.setAttribute('aria-hidden', 'true');
    overlay.setAttribute('inert', '');
  }

  /**
   * Portal the overlay to <body> at init so it lives at document-root level.
   * Prevents stacking-context leakage from the surrounding header-group
   * wrapper (announcement bar, sticky header) — overlay always wins z-index
   * unconditionally. Same pattern as nav-mobile-drawer.
   */
  function portalOverlayToBody() {
    const overlay = getOverlay();
    if (overlay && overlay.parentNode !== document.body) {
      document.body.appendChild(overlay);
    }
  }

  function init() {
    portalOverlayToBody();
    const overlay = getOverlay();
    if (!overlay) return;
    overlay.addEventListener('click', onBackdropClick);
    overlay.addEventListener('click', onChipClick);
    document.addEventListener('keydown', handleGlobalKey);
    bindTriggers();
    patchFetchForSearch();
    // bfcache restore safety-net: if the page is served from back/forward cache
    // with overlay state still attached (e.g. a chip click navigated away
    // before closeOverlay ran), force the clean state on pageshow.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) resetOverlayState();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ChaosNavSearch = { open: openOverlay, close: closeOverlay };
})();
